import React, { FC, InputHTMLAttributes, ReactNode } from 'react';
import './Radio.scss';
import { Radio as MantineRadio } from '@mantine/core';
import clsx from 'clsx';
import { toMantineError } from '../../theme/forms';
import { useFieldA11y } from '../../hooks/useFieldA11y';
import { Variant } from '../../types/ColorVariants';

// Omit 'size': HTML size:number конфликтует с Mantine size.
export interface IRadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
    /** Ссылка на input-элемент */
    ref?: React.Ref<HTMLInputElement>;
    /** Лейбл */
    label: ReactNode;
    /** Значение; число — у числовых полей формы (RadioControl CP с `numberType`), в DOM уходит строкой */
    value: string | number;
    /**
     * Отображение иконки
     * @deprecated Mantine всегда рисует кружок; проп игнорируется.
     */
    icon?: boolean;
    /**
     * Вариант
     * @deprecated
     */
    variant?: Variant;
    /**
     * 100% ширины
     * @default true
     */
    fullWidth?: boolean;
    /** Невалидный статус (текст ошибки рендерит FormGroup) */
    invalid?: boolean;
    /** Только для чтения: подпись читается, а не гаснет, как у недоступной кнопки (выбор блокирует `disabled`) */
    readOnly?: boolean;
}

// Фасад на Mantine Radio. Публичный IRadioProps заморожен ради потребителей (RadioControl +
// VoteQuestion×2). Самодельная разметка (label>input + кастомный кружок) заменена на нативный
// Mantine Radio (role=radio, a11y). invalid→error (toMantineError). checked/onChange/value —
// сквозные (controlled через RadioControl). fullWidth (default true) → width:100% классом.
// icon/variant — deprecated, игнорируются (у Mantine Radio свои icon/variant другого типа).
const Radio: FC<IRadioProps> = ({
    ref,
    label,
    value,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    icon = true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    variant,
    fullWidth = true,
    invalid,
    readOnly,
    className,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    ...props
}) => {
    const fieldA11y = useFieldA11y(
        { 'aria-label': props['aria-label'], 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy },
        label != null && label !== '',
    );
    return (
        <MantineRadio
            {...props}
            {...fieldA11y}
            aria-invalid={invalid || props['aria-invalid'] || undefined}
            ref={ref}
            readOnly={readOnly}
            value={value}
            label={label}
            error={toMantineError(invalid)}
            className={clsx(
                'rf-radio',
                fullWidth && 'rf-radio--fullwidth',
                readOnly && 'rf-radio--readonly',
                className,
            )}
        />
    );
};

export default Radio;
